import { CheckCircle, RefreshCw, Wifi, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ConnectivityTestResult } from '../../types/velociraptor-settings'
import { SectionCard } from './SectionCard'

interface TestButtonProps {
  testing: boolean
  onTest: () => void
}

function TestButton({ testing, onTest }: TestButtonProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <button
      onClick={onTest}
      disabled={testing}
      className="inline-flex items-center gap-2 rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:opacity-50"
    >
      {testing ? (
        <>
          <RefreshCw className="h-4 w-4 animate-spin" />
          {t('velociraptorSettings.testing')}
        </>
      ) : (
        <>
          <Wifi className="h-4 w-4" />
          {t('velociraptorSettings.testConnection')}
        </>
      )}
    </button>
  )
}

interface ConnectivityRowsProps {
  result: ConnectivityTestResult
}

function ConnectivityRows({ result }: ConnectivityRowsProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const yes = t('velociraptorSettings.connectivity.yes')
  const no = t('velociraptorSettings.connectivity.no')
  return (
    <div className="mt-2 grid gap-2 text-xs">
      <div className="flex justify-between">
        <span className="text-stone-500 dark:text-stone-400">
          {t('velociraptorSettings.connectivity.apiReachable')}
        </span>
        <span>{result.apiReachable ? yes : no}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-stone-500 dark:text-stone-400">
          {t('velociraptorSettings.connectivity.guiReachable')}
        </span>
        <span>{result.guiReachable ? yes : no}</span>
      </div>
      {result.latencyMs !== null && (
        <div className="flex justify-between">
          <span className="text-stone-500 dark:text-stone-400">
            {t('velociraptorSettings.connectivity.latency')}
          </span>
          <span>{String(result.latencyMs)}ms</span>
        </div>
      )}
      {result.velociraptorVersion !== null && (
        <div className="flex justify-between">
          <span className="text-stone-500 dark:text-stone-400">
            {t('velociraptorSettings.connectivity.serverVersion')}
          </span>
          <span className="font-mono">{result.velociraptorVersion}</span>
        </div>
      )}
    </div>
  )
}

interface ConnectivityResultPanelProps {
  result: ConnectivityTestResult
}

function ConnectivityResultPanel({ result }: ConnectivityResultPanelProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const containerClass = result.success
    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
  const titleClass = result.success
    ? 'text-green-800 dark:text-green-200'
    : 'text-red-800 dark:text-red-200'
  const headline = result.success
    ? t('velociraptorSettings.connectionSuccessful')
    : t('velociraptorSettings.connectionFailed')
  return (
    <div className={`rounded-lg p-4 ${containerClass}`}>
      <div className="flex items-start gap-3">
        {result.success ? (
          <CheckCircle className="mt-0.5 h-5 w-5 text-green-600 dark:text-green-400" />
        ) : (
          <XCircle className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" />
        )}
        <div className="flex-1">
          <p className={`text-sm font-medium ${titleClass}`}>{headline}</p>
          <ConnectivityRows result={result} />
          {result.errorMessage !== null && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{result.errorMessage}</p>
          )}
          {result.errorResolution !== null && (
            <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
              {t('velociraptorSettings.connectivity.suggestion')}: {result.errorResolution}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

interface ConnectivityTestCardProps {
  testing: boolean
  testResult: ConnectivityTestResult | null
  onTest: () => void
}

export function ConnectivityTestCard({
  testing,
  testResult,
  onTest,
}: ConnectivityTestCardProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <SectionCard
      title={t('velociraptorSettings.connectivityTest')}
      description={t('velociraptorSettings.connectivityTestDesc')}
      icon={Wifi}
    >
      <div className="space-y-4">
        <TestButton testing={testing} onTest={onTest} />
        {testResult && <ConnectivityResultPanel result={testResult} />}
      </div>
    </SectionCard>
  )
}
